import { useState, useCallback } from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Typography,
  useTheme,
} from "@mui/material";
import {
  buttonsContainerStyles,
  errorTypographyStyles,
  fieldContainerStyles,
  mainButtonStyles,
  modalBoxStyles,
  secondaryButtonStyles,
} from "./styles";
import { AxiosError } from "axios";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { BsFiletypeCsv } from "react-icons/bs";
import { useDropzone } from "react-dropzone";
import { CustomTypography } from "../../custom-typography/custom-typography";
import { uploadUserCsv } from "../../../axios/admin";
import { AdminCreds } from "../../../types";

interface CsvUploadModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (res: string) => void;
  adminCreds: AdminCreds;
}

const getErrorMessage = (error: unknown, defaultMessage?: string): string => {
  if (error && typeof error === "object") {
    if (error instanceof AxiosError) {
      if (error.response?.data.detail) return error.response?.data.detail;
    } else if ("message" in error && typeof error.message === "string") {
      return error.message;
    }
  } else if (typeof error === "string") {
    return error;
  }

  if (defaultMessage) return defaultMessage;

  return "Oops... Unknown error happened while sending a file";
};

export const CsvUploadModal = ({
  open,
  onClose,
  onSuccess,
  adminCreds,
}: CsvUploadModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const theme = useTheme();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFile(acceptedFiles[0]);
    setFormError(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "text/csv": [".csv"] },
    multiple: false,
  });

  const handleRemoveFile = () => {
    setFile(null);
    setFormError(null);
  };

  const handleClose = () => {
    setFile(null);
    setFormError(null);
    onClose();
  };

  const handleSend = async () => {
    if (!file) {
      setFormError("Upload a file first");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await uploadUserCsv(formData, adminCreds);
      setFile(null);
      onSuccess(res);
      onClose();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.error("Error sending a csv-file:", errorMessage);
      setFormError(errorMessage);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalBoxStyles}>
        <Box sx={fieldContainerStyles}>
          <CustomTypography
            fontSize={20}
            fontWeight={600}
            sx={{
              color: theme.palette.modalColors.headerColor,
            }}
          >
            Upload a CSV File
          </CustomTypography>

          {formError && (
            <Typography variant="body2" sx={errorTypographyStyles}>
              {formError}
            </Typography>
          )}
        </Box>

        <form className="flex flex-col gap-[16px]">
          <Box
            {...getRootProps()}
            sx={{
              border: "2px dashed #aaa",
              padding: 3,
              textAlign: "center",
              borderRadius: 2,
              cursor: "pointer",
              backgroundColor: isDragActive ? "#f5f5f5" : "transparent",
            }}
          >
            <input {...getInputProps()} />
            {!file ? (
              <Box
                sx={{
                  color: theme.palette.modalColors.colorLable,
                }}
              >
                <UploadFileIcon fontSize="large" />
                <CustomTypography variant="body1">
                  Drag & drop a csv-file here, or click to select
                </CustomTypography>
              </Box>
            ) : (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
              >
                <CustomTypography
                  fontSize={12}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "10px",
                    alignItems: "center",
                  }}
                >
                  <BsFiletypeCsv
                    style={{
                      transform: "scale(1.5, 1.5)",
                    }}
                  />
                  <span>{file.name}</span>
                </CustomTypography>
                <IconButton
                  onClick={handleRemoveFile}
                  size="small"
                  sx={{
                    background: theme.palette.adminPanel.mainBg,
                    transition: "opacity ease 0.3s",
                    "&:hover": {
                      opacity: 0.7,
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>

          <Box sx={buttonsContainerStyles}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={secondaryButtonStyles}
            >
              Close
            </Button>
            <Button
              variant="contained"
              onClick={handleSend}
              sx={mainButtonStyles}
            >
              Send
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};
